// @mui material components
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import { Box, Card, CardContent, Tooltip, Chip, Divider, Typography, Paper, TextField, CircularProgress, Snackbar, Alert, IconButton, Icon, Button, Backdrop } from "@mui/material";
import Lottie from "react-lottie";
import * as animationData from "./../../assets/loader/loader.json";
import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import handleExport from "utils/exportData";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Icons
import GroupIcon from "@mui/icons-material/Group";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import GroupOffIcon from "@mui/icons-material/GroupOff";
import GroupRemoveIcon from "@mui/icons-material/GroupRemove";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AssessmentIcon from "@mui/icons-material/Assessment";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { IconTableExport } from "@tabler/icons-react";

// ============================================================================
// ENTERPRISE COLOR PALETTE
// ============================================================================
const COLORS = {
  text: {
    primary: "#1e293b",
    secondary: "#475569",
    tertiary: "#64748b",
    muted: "#94a3b8",
  },
  status: {
    success: {
      main: "#10b981",
      bg: "#ecfdf5",
      border: "#d1fae5",
      gradient: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
    },
    warning: {
      main: "#f59e0b",
      bg: "#fffbeb",
      border: "#fef3c7",
      gradient: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)",
    },
    error: {
      main: "#ef4444",
      bg: "#fef2f2",
      border: "#fee2e2",
      gradient: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
    },
    info: {
      main: "#3b82f6",
      bg: "#eff6ff",
      border: "#dbeafe",
      gradient: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    },
    purple: {
      main: "#8b5cf6",
      bg: "#f5f3ff",
      border: "#ede9fe",
      gradient: "linear-gradient(135deg, #f5f3ff 0%, #ede9fe 100%)",
    },
    neutral: {
      main: "#64748b",
      bg: "#f8fafc",
      border: "#f1f5f9",
      gradient: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    },
  },
  background: {
    main: "#f8fafc",
    card: "#ffffff",
    hover: "#f1f5f9",
    light: "#f8fafc",
  },
  border: {
    light: "#e2e8f0",
    medium: "#cbd5e1",
  },
};

// ============================================================================
// SECTION THEME PALETTES — one per dashboard section
// ============================================================================
const SECTION_THEMES = {
  // Monthly Submission Reports — Deep Coral/Tomato
  monthly: {
    accent: "#c2410c",
    accentLight: "#ea580c",
    bg: "#fff7ed",
    border: "#fed7aa",
    cardBg: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
    headerBar: "#c2410c",
    subtitleColor: "#9a3412",
    chipBg: "#fed7aa",
    chipColor: "#7c2d12",
  },
  // Claims Submission Reports — Deep Indigo/Violet
  claims: {
    accent: "#4338ca",
    accentLight: "#6366f1",
    bg: "#eef2ff",
    border: "#c7d2fe",
    cardBg: "linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)",
    headerBar: "#4338ca",
    subtitleColor: "#3730a3",
    chipBg: "#c7d2fe",
    chipColor: "#312e81",
  },
  // Attendance Overview — Fuchsia/Pink
  attendance: {
    accent: "#a21caf",
    accentLight: "#c026d3",
    bg: "#fdf4ff",
    border: "#f0abfc",
    cardBg: "linear-gradient(135deg, #fdf4ff 0%, #fae8ff 100%)",
    headerBar: "#a21caf",
    subtitleColor: "#86198f",
    chipBg: "#f0abfc",
    chipColor: "#701a75",
  },
  // Lessons from the Field — Warm Brown/Sienna (previously date range color)
  lessons: {
    accent: "#92400e",
    accentLight: "#b45309",
    bg: "#fefce8",
    border: "#fde68a",
    cardBg: "linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)",
    headerBar: "#92400e",
    subtitleColor: "#78350f",
    chipBg: "#fde68a",
    chipColor: "#713f12",
  },
  // Date Range Filter — White/Neutral
  dateFilter: {
    accent: "#64748b",
    accentLight: "#94a3b8",
    bg: "#ffffff",
    border: "#e2e8f0",
    cardBg: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
    headerBar: "#64748b",
    subtitleColor: "#475569",
    chipBg: "#f1f5f9",
    chipColor: "#1e293b",
  },
  // Action Required — Dark Lime/Olive
  action: {
    accent: "#4d7c0f",
    accentLight: "#65a30d",
    bg: "#f7fee7",
    border: "#bef264",
    cardBg: "linear-gradient(135deg, #f7fee7 0%, #ecfccb 100%)",
    headerBar: "#4d7c0f",
    subtitleColor: "#3f6212",
    chipBg: "#d9f99d",
    chipColor: "#365314",
  },
  // Lessons from the Field — Steel Blue/Cerulean
  lessons: {
    accent: "#0369a1",
    accentLight: "#0284c7",
    bg: "#f0f9ff",
    border: "#bae6fd",
    cardBg: "linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)",
    headerBar: "#0369a1",
    subtitleColor: "#075985",
    chipBg: "#bae6fd",
    chipColor: "#0c4a6e",
  },
  // Date Range Filter — Warm Brown/Sienna
  dateFilter: {
    accent: "#92400e",
    accentLight: "#b45309",
    bg: "#fefce8",
    border: "#fde68a",
    cardBg: "linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)",
    headerBar: "#92400e",
    subtitleColor: "#78350f",
    chipBg: "#fde68a",
    chipColor: "#713f12",
  },
  // Action Required — Deep Orange/Amber
  action: {
    accent: "#c2410c",
    accentLight: "#ea580c",
    bg: "#fff7ed",
    border: "#fed7aa",
    cardBg: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
    headerBar: "#c2410c",
    subtitleColor: "#9a3412",
    chipBg: "#fed7aa",
    chipColor: "#7c2d12",
  },
  // Salary Information — keeps white bg, accent is Deep Teal
  salary: {
    accent: "#0f766e",
    accentLight: "#0d9488",
    bg: "#f0fdfa",
    border: "#99f6e4",
    cardBg: "linear-gradient(135deg, #f0fdfa 0%, #ccfbf1 100%)",
    headerBar: "#0f766e",
    subtitleColor: "#115e59",
    chipBg: "#99f6e4",
    chipColor: "#134e4a",
  },
};

// ============================================================================
// CLEAN METRIC CARD COMPONENT
// ============================================================================
const MetricCard = ({
  title,
  subtitle,
  value,
  icon: IconComponent,
  color = "info",
  tooltipContent,
  onClick,
  showAction = false,
  width
}) => {
  const isClickable = !!onClick;
  const statusColor = COLORS.status[color] || COLORS.status.info;

  return (
    <Tooltip
      title={tooltipContent || ""}
      disableHoverListener={!tooltipContent}
      arrow
      placement="top"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: "white",
            color: COLORS.text.primary,
            boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
            border: `1px solid ${COLORS.border.light}`,
            borderRadius: "12px",
            p: 2,
            maxWidth: 600,
            "& .MuiTooltip-arrow": {
              color: "white",
              "&::before": {
                border: `1px solid ${COLORS.border.light}`,
              },
            },
          },
        },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          height: "100%",
          width: width || "100%",
          minWidth: "210px",
          minHeight: "160px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          bgcolor: COLORS.background.card,
          background: statusColor.gradient,
          border: `1px solid ${statusColor.border}`,
          borderRadius: "16px",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          cursor: isClickable ? "pointer" : "default",
          position: "relative",
          overflow: "hidden",
          "&::after": {
            content: '""',
            position: "absolute",
            top: 0,
            right: 0,
            width: "60%",
            height: "100%",
            background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.2) 100%)",
            pointerEvents: "none",
          },
          "&:hover": isClickable ? {
            borderColor: statusColor.main,
            boxShadow: `0 20px 25px -5px rgba(0,0,0,0.05), 0 10px 10px -5px rgba(0,0,0,0.04)`,
            transform: "translateY(-4px)",
          } : {},
        }}
        onClick={onClick}
      >
        <CardContent sx={{ p: 3, "&:last-child": { pb: 3 } }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
            <Box
              sx={{
                width: 200,
                height: 44,
                borderRadius: "12px",
                bgcolor: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
                border: `1px solid ${statusColor.border}`,
              }}
            >
              <IconComponent sx={{ fontSize: 22, color: statusColor.main }} />
            </Box>

            {showAction && (
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                }}
              >
                <ArrowForwardIcon
                  sx={{
                    fontSize: 16,
                    color: statusColor.main,
                  }}
                />
              </Box>
            )}
          </Box>

          <Box>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                fontSize: "28px",
                color: COLORS.text.primary,
                mb: 0.5,
                letterSpacing: "-1px",
              }}
            >
              {value}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: COLORS.text.secondary,
                opacity: 0.8,
              }}
            >
              {title}
            </Typography>

            {subtitle && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: "11px",
                  fontWeight: 500,
                  color: COLORS.text.tertiary,
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                  mt: 0.5,
                  display: "block"
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </CardContent>
      </Paper>
    </Tooltip>
  );
};

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.oneOf(["success", "warning", "error", "info", "neutral"]),
  tooltipContent: PropTypes.node,
  onClick: PropTypes.func,
  showAction: PropTypes.bool,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// ============================================================================
// SPLIT STATUS CARD COMPONENT (for reports with multiple statuses)
// ============================================================================
const SplitStatusCard = ({
  title,
  icon: IconComponent,
  statuses,
  onClick,
  color = "info",
  width
}) => {
  const statusColor = COLORS.status[color] || COLORS.status.info;
  return (
    <Paper
      elevation={0}
      sx={{
        height: "100%",
        width: width || "100%",
        bgcolor: COLORS.background.card,
        border: `1px solid ${COLORS.border.light}`,
        borderRadius: "16px",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        cursor: onClick ? "pointer" : "default",
        overflow: "hidden",
        "&:hover": onClick ? {
          borderColor: COLORS.border.medium,
          boxShadow: "0 20px 25px -5px rgba(0,0,0,0.05)",
          transform: "translateY(-4px)",
        } : {},
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={2} mb={4}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              bgcolor: statusColor.bg,
              border: `1px solid ${statusColor.border}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
            }}
          >
            <IconComponent sx={{ fontSize: 24, color: statusColor.main }} />
          </Box>

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              fontSize: "16px",
              color: COLORS.text.primary,
              letterSpacing: "-0.3px",
            }}
          >
            {title}
          </Typography>
        </Box>

        {/* Status Grid */}
        <Box display="flex" gap={2} flex={1}>
          {statuses.map((status, index) => {
            const statusType = status.color || (index === 0 ? "warning" : "error");
            const statusConfig = COLORS.status[statusType] || COLORS.status.neutral;

            return (
              <Tooltip
                key={index}
                title={status.tooltipContent || ""}
                disableHoverListener={!status.tooltipContent}
                arrow
                placement="top"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "white",
                      color: COLORS.text.primary,
                      boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1)",
                      border: `1px solid ${COLORS.border.light}`,
                      borderRadius: "10px",
                      p: 2,
                      maxWidth: 280,
                    },
                  },
                }}
              >
                <Box
                  onClick={(e) => {
                    if (status.onClick) {
                      e.stopPropagation();
                      status.onClick();
                    }
                  }}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    bgcolor: statusConfig.bg,
                    background: statusConfig.gradient,
                    border: `1px solid ${statusConfig.border}`,
                    borderRadius: "14px",
                    p: 2.5,
                    textAlign: "center",
                    cursor: status.onClick ? "pointer" : "default",
                    transition: "all 0.2s",
                    "&:hover": status.onClick ? {
                      transform: "scale(1.02)",
                      borderColor: statusConfig.main,
                      boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)",
                    } : {},
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      fontSize: "26px",
                      color: COLORS.text.primary,
                      mb: 0.5,
                      letterSpacing: "-0.5px",
                    }}
                  >
                    {status.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: COLORS.text.secondary,
                      textTransform: "uppercase",
                      letterSpacing: "1px",
                      opacity: 0.8,
                    }}
                  >
                    {status.label}
                  </Typography>
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </CardContent>
    </Paper>
  );
};

SplitStatusCard.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  color: PropTypes.string,
  statuses: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      color: PropTypes.string,
      tooltipContent: PropTypes.node,
      onClick: PropTypes.func,
    })
  ).isRequired,
  onClick: PropTypes.func,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

// ============================================================================
// SECTION HEADER COMPONENT — now accepts a `theme` prop for per-section color
// ============================================================================
const SectionHeader = ({ title, subtitle, theme }) => {
  const accentColor = theme?.headerBar || COLORS.status.info.main;
  return (
    <Box mb={4}>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          fontSize: "18px",
          color: COLORS.text.primary,
          mb: 0.75,
          letterSpacing: "-0.3px",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          "&::before": {
            content: '""',
            width: "4px",
            height: "20px",
            bgcolor: accentColor,
            borderRadius: "4px",
          }
        }}
      >
        {title || ""}
      </Typography>
      {subtitle && (
        <Typography
          variant="body2"
          sx={{
            fontSize: "14px",
            color: theme?.subtitleColor || COLORS.text.tertiary,
            fontWeight: 500,
            ml: 2,
          }}
        >
          {subtitle}
        </Typography>
      )}
      <Divider sx={{ mt: 2.5, opacity: 0.15, borderColor: accentColor }} />
    </Box>
  );
};

SectionHeader.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  theme: PropTypes.object,
};

// ============================================================================
// USER LIST TOOLTIP COMPONENT
// ============================================================================
const UserListTooltip = ({ users }) => {
  if (!users) return null;

  const userArray = users.split(",").map(name => name.trim());

  return (
    <Box sx={{ maxHeight: 240, overflowY: "auto" }}>
      {userArray.map((name, idx) => (
        <Box
          key={idx}
          sx={{
            py: 0.75,
            px: 1.5,
            mb: 0.5,
            bgcolor: COLORS.background.light,
            borderRadius: "6px",
            border: `1px solid ${COLORS.border.light}`,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "12px",
              fontWeight: 500,
              color: COLORS.text.primary,
            }}
          >
            {name}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

UserListTooltip.propTypes = {
  users: PropTypes.string,
};

// ============================================================================
// SECTION WRAPPER — applies a colored background + left border accent
// ============================================================================
const SectionPaper = ({ children, theme, sx = {} }) => (
  <Paper
    elevation={0}
    sx={{
      mb: 4,
      p: 3,
      bgcolor: theme?.bg || COLORS.background.card,
      border: `1px solid ${theme?.border || COLORS.border.light}`,
      borderLeft: `4px solid ${theme?.accent || COLORS.status.info.main}`,
      borderRadius: "12px",
      ...sx,
    }}
  >
    {children}
  </Paper>
);

SectionPaper.propTypes = {
  children: PropTypes.node.isRequired,
  theme: PropTypes.object,
  sx: PropTypes.object,
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================
function Dashboard() {
  const navigate = useNavigate();

  const defaultOptions = {
    loop: true,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };
  const [fromDate, setFromDate] = useState(dayjs().startOf('month'));
  const [toDate, setToDate] = useState(dayjs().endOf('month'));
  const [reportMonth, setReportMonth] = useState(dayjs().startOf('month'));
  const [attendanceData, setAttendanceData] = useState({});
  const [salaryFilters, setSalaryFilters] = useState({
    status: "All",
    user_id: "",
    district_id: "",
    monthFilters: {},
  });
  const [salarySorting, setSalarySorting] = useState({ column: null, direction: 'asc' });
  const [exportSuccess, setExportSuccess] = useState(false);

  const handleCloseAlert = () => setExportSuccess(false);

  // Fetch attendance data
  useEffect(() => {
    const fetchAttendance = async () => {
      try {
        const queryParams = new URLSearchParams({
          start_date: dayjs().format("DD-MM-YYYY"),
          end_date: dayjs().format("DD-MM-YYYY"),
        });

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/dashboard/attendance?${queryParams.toString()}`,
          { withCredentials: true }
        );

        if (res.data.status === 200 && res.data.data?.length > 0) {
          setAttendanceData(res.data.data[0]);
        }
      } catch (error) {
        console.error("Error fetching attendance:", error);
      }
    };

    fetchAttendance();
  }, []);

  // Fetch user info
  const me = async () => {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/auth/me`,
      headers: { "Content-Type": "application/json" },
      withCredentials: true,
    });
    return response.data;
  };

  const { data: meData, isLoading: isMeLoading } = useQuery({
    queryKey: ["me"],
    queryFn: me,
    retry: false,
  });
  const { data: latestLessons, isLoading: isLessonsLoading } = useQuery({
    queryKey: ["latest-lessons"],
    queryFn: async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/latest-lesson`,
        { withCredentials: true }
      );
      const data = res.data.data;
      return Array.isArray(data) ? data : (data ? [data] : []);
    },
  });

  const { data: adminStats } = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/admin-stats`,
        { withCredentials: true }
      );
      return res.data.data;
    },
    enabled: !!meData && (meData.role_id === 1 || meData.role_id === 2),
  });

  const { data: submissionStats, isFetching: isFetchingSubmission } = useQuery({
    queryKey: ["submission-count", reportMonth],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        start_month: reportMonth.format("MMMM"),
        end_month: reportMonth.format("MMMM"),
        year: reportMonth.format("YYYY"),
      });

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/dashboard/submission-count?${queryParams.toString()}`,
        { withCredentials: true }
      );
      return res.data.data;
    },
    enabled: !!meData && (meData.role_id === 1 || meData.role_id === 2),
  });

  const { data: usersData } = useQuery({
    queryKey: ["fetchAllUsers"],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/user/role3`, { withCredentials: true });
      return response.data.data;
    },
    enabled: !!meData && (meData.role_id === 1 || meData.role_id === 2),
  });

  const { data: districtData } = useQuery({
    queryKey: ["fetchAllDistricts"],
    queryFn: async () => {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/districts/all`, { withCredentials: true });
      return response.data.data;
    },
    enabled: !!meData && (meData.role_id === 1 || meData.role_id === 2),
  });

  const { data: salaryStats, isLoading: isSalaryLoading, isFetching: isFetchingSalary } = useQuery({
    queryKey: ["salary-list", fromDate, toDate, salaryFilters],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        from_month: fromDate.format("YYYY-MM"),
        to_month: toDate.format("YYYY-MM"),
        status: salaryFilters.status !== "All" ? salaryFilters.status : "",
        user_id: salaryFilters.user_id,
        district_id: salaryFilters.district_id,
      });

      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/salary/list?${queryParams.toString()}`,
        { withCredentials: true }
      );
      return res.data;
    },
    enabled: !!meData && (meData.role_id === 1 || meData.role_id === 2),
  });

  const getMonthRange = () => {
    let months = [];
    let current = fromDate.startOf("month");
    const end = toDate.startOf("month");

    while (current.isBefore(end) || current.isSame(end)) {
      months.push(current.format("YYYY-MM"));
      current = current.add(1, "month");
    }
    return months;
  };

  const monthColumns = getMonthRange();

  const StatusIndicator = ({ status }) => {
    const isReceived = status === "Received";
    return (
      <Chip
        label={isReceived ? "Received" : "Not received"}
        size="small"
        sx={{
          height: "22px",
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          borderRadius: "6px",
          bgcolor: isReceived ? COLORS.status.success.bg : COLORS.status.error.bg,
          color: isReceived ? COLORS.status.success.main : COLORS.status.error.main,
          border: `1px solid ${isReceived ? COLORS.status.success.border : COLORS.status.error.border}`,
          "& .MuiChip-label": { px: 1 },
        }}
      />
    );
  };

  StatusIndicator.propTypes = {
    status: PropTypes.string.isRequired,
  };

  const handleExportSalary = () => {
    if (!salaryStats?.data) return;

    const exportRows = salaryStats.data.map(user => {
      const row = {
        "User Name": user.user_name,
        "District": user.district_name,
      };
      monthColumns.forEach(m => {
        const history = user.salary_history?.find(h => h.month === m);
        row[dayjs(m).format("MMM YYYY")] = history ? history.status : "Not received";
      });
      return row;
    });

    handleExport(exportRows, "Green_Fellows_Salary_Report");
    setExportSuccess(true);
  };

  const handleSort = (column) => {
    setSalarySorting(prev => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedAndFilteredData = useMemo(() => {
    if (!salaryStats?.data) return [];

    let filtered = [...salaryStats.data];

    Object.keys(salaryFilters.monthFilters).forEach(month => {
      const statusFilter = salaryFilters.monthFilters[month];
      if (statusFilter && statusFilter !== "All") {
        filtered = filtered.filter(user => {
          const history = user.salary_history?.find(h => h.month === month);
          const status = history?.status?.toLowerCase() === "received" ? "Received" : "Not received";
          return status === statusFilter;
        });
      }
    });

    if (salarySorting.column) {
      filtered.sort((a, b) => {
        let valA = a[salarySorting.column] || "";
        let valB = b[salarySorting.column] || "";

        if (salarySorting.column === "user_name" || salarySorting.column === "district_name") {
          return salarySorting.direction === 'asc'
            ? valA.localeCompare(valB)
            : valB.localeCompare(valA);
        }
        return 0;
      });
    }

    return filtered;
  }, [salaryStats, salarySorting, salaryFilters.monthFilters]);

  if (meData && meData.role_id === 3) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        flexDirection="column"
        bgcolor={COLORS.background.main}
      >
        <Box textAlign="center">
          <Typography variant="h3" sx={{ fontWeight: 600, mb: 1, color: COLORS.text.primary }}>
            403
          </Typography>
          <Typography variant="h6" sx={{ mb: 2, color: COLORS.text.secondary, fontWeight: 500 }}>
            Access Denied
          </Typography>
          <Typography variant="body1" sx={{ color: COLORS.text.tertiary }}>
            You don&apos;t have permission to access this dashboard.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (isMeLoading) {
    return (
      <DashboardLayout>
        <MDBox display="flex" justifyContent="center" alignItems="center" height="60vh">
          <Typography sx={{ color: COLORS.text.secondary }}>Loading...</Typography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box
        sx={{
          bgcolor: COLORS.background.main,
          minHeight: "100vh",
          py: 4,
          px: { xs: 2.5, md: 4 },
        }}
      >
        {/* ===== MONTHLY SUBMISSION REPORTS — Violet ===== */}
        <SectionPaper theme={SECTION_THEMES.monthly}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={4} flexWrap="wrap" gap={2}>
            <SectionHeader
              title="Monthly Submission Reports"
              subtitle="Track weekly, mission, and claim report submissions for a specific month"
              theme={SECTION_THEMES.monthly}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ minWidth: "200px" }}>
                <DatePicker
                  label="Select Month/Year"
                  views={['year', 'month']}
                  value={reportMonth}
                  onChange={(newValue) => setReportMonth(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        "& .MuiInputBase-root": {
                          bgcolor: "white",
                          borderRadius: "8px",
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "13px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: SECTION_THEMES.monthly.border,
                        },
                        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: SECTION_THEMES.monthly.accent,
                        },
                      }
                    }
                  }}
                />
              </Box>
            </LocalizationProvider>
          </Box>

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={6}>
              <SplitStatusCard
                width="100%"
                title="Weekly Reports"
                icon={AssessmentIcon}
                color="purple"
                statuses={[
                  {
                    label: "Completed",
                    value: submissionStats?.weekly_reports?.submitted ?? 0,
                    color: "success",
                    onClick: () => navigate("/weekly-report", {
                      state: { status: "Submitted", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                  {
                    label: "Pending",
                    value: submissionStats?.weekly_reports?.pending ?? 0,
                    tooltipContent: <UserListTooltip users={submissionStats?.weekly_reports?.pending_users} />,
                    onClick: () => navigate("/weekly-report", {
                      state: { status: "Pending", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                  {
                    label: "Not Submitted",
                    value: submissionStats?.weekly_reports?.not_submitted ?? 0,
                    tooltipContent: <UserListTooltip users={submissionStats?.weekly_reports?.not_submitted_users} />,
                    onClick: () => navigate("/weekly-report", {
                      state: { status: "Not Submitted", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                ]}
                onClick={() => navigate("/weekly-report", {
                  state: { month: reportMonth.month() + 1, year: reportMonth.year() }
                })}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <SplitStatusCard
                title="Mission Reports"
                icon={AssessmentIcon}
                width="100%"
                statuses={[
                  {
                    label: "Completed",
                    value: submissionStats?.reports?.submitted ?? 0,
                    color: "success",
                    onClick: () => navigate("/mission-tracker", {
                      state: { status: "Submitted", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                  {
                    label: "Not Submitted",
                    value: submissionStats?.reports?.not_submitted ?? 0,
                    color: "error",
                    tooltipContent: <UserListTooltip users={submissionStats?.reports?.not_submitted_users} />,
                    onClick: () => navigate("/mission-tracker", {
                      state: { status: "Not Submitted", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                ]}
              />
            </Grid>
          </Grid>
        </SectionPaper>

        {/* ===== CLAIMS SUBMISSION REPORTS — Teal ===== */}
        <SectionPaper theme={SECTION_THEMES.claims}>
          <SectionHeader
            title="Claims Submission Reports"
            subtitle="Detailed breakdown of claim document submissions"
            theme={SECTION_THEMES.claims}
          />

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={4}>
              <SplitStatusCard
                title="Overall Claims"
                icon={ReceiptLongIcon}
                color="info"
                width="100%"
                statuses={[
                  {
                    label: "Total",
                    value: submissionStats?.claims?.total ?? 0,
                    color: "info",
                  },
                  {
                    label: "Not Submitted",
                    value: submissionStats?.claims?.not_submitted ?? 0,
                    color: "error",
                    tooltipContent: <UserListTooltip users={submissionStats?.claims?.not_submitted_users} />,
                    onClick: () => navigate("/claims/list", {
                      state: { status: "Not Submitted", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                ]}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <SplitStatusCard
                title="Attendance Sheet"
                icon={AssessmentIcon}
                color="info"
                width="100%"
                statuses={[
                  {
                    label: "Submitted",
                    value: submissionStats?.attendance_sheet?.submitted ?? 0,
                    color: "success",
                    tooltipContent: <UserListTooltip users={submissionStats?.attendance_sheet?.submitted_users} />,
                    onClick: () => navigate("/claims/list", {
                      state: { claimType: "attendance_sheet", status: "Submitted", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                  {
                    label: "Not Submitted",
                    value: submissionStats?.attendance_sheet?.not_submitted ?? 0,
                    color: "error",
                    tooltipContent: <UserListTooltip users={submissionStats?.attendance_sheet?.not_submitted_users} />,
                    onClick: () => navigate("/claims/list", {
                      state: { status: "Not Submitted", claimType: "attendance_sheet", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                ]}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <SplitStatusCard
                title="Stipend Claim"
                icon={ReceiptLongIcon}
                color="info"
                width="100%"
                statuses={[
                  {
                    label: "Submitted",
                    value: submissionStats?.stipend_claim?.submitted ?? 0,
                    color: "success",
                    tooltipContent: <UserListTooltip users={submissionStats?.stipend_claim?.submitted_users} />,
                    onClick: () => navigate("/claims/list", {
                      state: { claimType: "stipend_claim", status: "Submitted", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                  {
                    label: "Not Submitted",
                    value: submissionStats?.stipend_claim?.not_submitted ?? 0,
                    color: "error",
                    tooltipContent: <UserListTooltip users={submissionStats?.stipend_claim?.not_submitted_users} />,
                    onClick: () => navigate("/claims/list", {
                      state: { claimType: "stipend_claim", status: "Not Submitted", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                ]}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <SplitStatusCard
                title="Travel Bills"
                icon={AssessmentIcon}
                color="info"
                width="100%"
                statuses={[
                  {
                    label: "Submitted",
                    value: submissionStats?.travel_bills?.submitted ?? 0,
                    color: "success",
                    tooltipContent: <UserListTooltip users={submissionStats?.travel_bills?.submitted_users} />,
                    onClick: () => navigate("/claims/list", {
                      state: { claimType: "travel_bills", status: "Submitted", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                  {
                    label: "Not Submitted",
                    value: submissionStats?.travel_bills?.not_submitted ?? 0,
                    color: "error",
                    tooltipContent: <UserListTooltip users={submissionStats?.travel_bills?.not_submitted_users} />,
                    onClick: () => navigate("/claims/list", {
                      state: { claimType: "travel_bills", status: "Not Submitted", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                ]}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <SplitStatusCard
                title="Review TA Bills"
                icon={ReceiptLongIcon}
                color="info"
                width="100%"
                statuses={[
                  {
                    label: "Submitted",
                    value: submissionStats?.review_ta_bills?.submitted ?? 0,
                    color: "success",
                    tooltipContent: <UserListTooltip users={submissionStats?.review_ta_bills?.submitted_users} />,
                    onClick: () => navigate("/claims/list", {
                      state: { claimType: "review_ta_bills", status: "Submitted", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                  {
                    label: "Not Submitted",
                    value: submissionStats?.review_ta_bills?.not_submitted ?? 0,
                    color: "error",
                    tooltipContent: <UserListTooltip users={submissionStats?.review_ta_bills?.not_submitted_users} />,
                    onClick: () => navigate("/claims/list", {
                      state: { claimType: "review_ta_bills", status: "Not Submitted", month: reportMonth.month() + 1, year: reportMonth.year() }
                    }),
                  },
                ]}
              />
            </Grid>
          </Grid>
        </SectionPaper>

        {/* ===== ATTENDANCE OVERVIEW — Indigo ===== */}
        <SectionPaper theme={SECTION_THEMES.attendance}>
          <SectionHeader
            title="Attendance Overview"
            subtitle="Current fellows status and availability"
            theme={SECTION_THEMES.attendance}
          />

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={6}>
              <SplitStatusCard
                title="Total Green Fellows"
                icon={GroupIcon}
                color="info"
                width="390px"
                statuses={[
                  {
                    label: "Total",
                    value: adminStats?.totalUsers ?? 0,
                    color: "info",
                  },
                  {
                    label: "Active",
                    value: adminStats?.activeUsers ?? 0,
                    color: "success",
                  },
                  {
                    label: "Inactive",
                    value: adminStats?.inactiveUsers ?? 0,
                    color: "neutral",
                  },
                ]}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <MetricCard
                title="Present"
                subtitle="Today"
                value={attendanceData.present_count ?? 0}
                icon={GroupAddIcon}
                color="success"
                width="20px"
                tooltipContent={<UserListTooltip users={attendanceData.present_users} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <MetricCard
                title="Absent"
                subtitle="Today"
                width="20px"
                value={attendanceData.absent_count ?? 0}
                icon={GroupOffIcon}
                color="error"
                tooltipContent={<UserListTooltip users={attendanceData.absent_users} />}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={6}>
              <MetricCard
                title="On Leave"
                subtitle="Today"
                width="20px"
                value={attendanceData.leave_count ?? 0}
                icon={GroupRemoveIcon}
                color="warning"
                tooltipContent={<UserListTooltip users={attendanceData.leave_users} />}
              />
            </Grid>
          </Grid>
        </SectionPaper>

        {/* ===== LESSONS FROM THE FIELD — Amber ===== */}
        <SectionPaper theme={SECTION_THEMES.lessons}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <SectionHeader
              title="Lessons from the field"
              subtitle="Latest insights and findings from the field"
              theme={SECTION_THEMES.lessons}
            />
            <Button
              size="small"
              onClick={() => navigate("/lessons/list")}
              sx={{
                color: SECTION_THEMES.lessons.accent,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: SECTION_THEMES.lessons.chipBg }
              }}
              endIcon={<ArrowForwardIcon />}
            >
              View All
            </Button>
          </Box>

          <Grid container spacing={2.5}>
            {isLessonsLoading ? (
              <Grid item xs={12}>
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress size={24} sx={{ color: SECTION_THEMES.lessons.accent }} />
                </Box>
              </Grid>
            ) : latestLessons?.length > 0 ? (
              latestLessons.map((lesson) => (
                <Grid item xs={12} sm={6} md={4} key={lesson.id}>
                  <Paper
                    elevation={0}
                    onClick={() => navigate(`/lessons/details/${lesson.id}`)}
                    sx={{
                      p: 2.5,
                      height: "100%",
                      cursor: "pointer",
                      bgcolor: "white",
                      border: `1px solid ${SECTION_THEMES.lessons.border}`,
                      borderRadius: "12px",
                      transition: "all 0.2s ease-in-out",
                      display: "flex",
                      flexDirection: "column",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        borderColor: SECTION_THEMES.lessons.accent,
                        boxShadow: `0 10px 15px -3px rgba(146, 64, 14, 0.1)`,
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "15px",
                        fontWeight: 700,
                        color: COLORS.text.primary,
                        mb: 1.5,
                        lineHeight: 1.4,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "42px",
                      }}
                    >
                      {lesson.title}
                    </Typography>

                    <Box mt="auto" display="flex" alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: COLORS.text.secondary,
                            fontWeight: 600,
                            fontSize: "12px",
                            mb: 0.3
                          }}
                        >
                          <Icon sx={{ fontSize: "14px !important" }}>person</Icon>
                          {lesson.user_name}
                        </Typography>
                        {lesson.district_name && lesson.district_name !== "Headquarters" && (
                          <Typography
                            variant="caption"
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                              color: COLORS.status.success.main,
                              fontSize: "11px",
                              fontWeight: 700,
                            }}
                          >
                            <Icon sx={{ fontSize: "14px !important" }}>place</Icon>
                            {lesson.district_name}
                          </Typography>
                        )}
                      </Box>
                      <IconButton size="small" sx={{ color: SECTION_THEMES.lessons.accent }}>
                        <ArrowForwardIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Paper>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography variant="body2" sx={{ color: COLORS.text.muted, textAlign: "center", py: 4 }}>
                  No lessons found.
                </Typography>
              </Grid>
            )}
          </Grid>
        </SectionPaper>

        {/* ===== DATE RANGE FILTERS — Slate ===== */}
        <SectionPaper
          theme={SECTION_THEMES.dateFilter}
          sx={{
            px: 3,
            py: 2,
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 3,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "16px",
              color: COLORS.text.primary,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              whiteSpace: "nowrap",
              "&::before": {
                content: '""',
                width: "4px",
                height: "18px",
                bgcolor: SECTION_THEMES.dateFilter.accent,
                borderRadius: "4px",
              }
            }}
          >
            Date Range Filter
          </Typography>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box display="flex" alignItems="center" gap={2} flex={1} justifyContent="flex-end">
              <Box sx={{ minWidth: "180px" }}>
                <DatePicker
                  label="From Month"
                  views={['year', 'month']}
                  value={fromDate}
                  onChange={(newValue) => setFromDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        "& .MuiInputBase-root": {
                          bgcolor: "white",
                          borderRadius: "8px",
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "13px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: SECTION_THEMES.dateFilter.border,
                        },
                      }
                    }
                  }}
                />
              </Box>
              <Box sx={{ minWidth: "180px" }}>
                <DatePicker
                  label="To Month"
                  views={['year', 'month']}
                  value={toDate}
                  onChange={(newValue) => setToDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: "small",
                      sx: {
                        "& .MuiInputBase-root": {
                          bgcolor: "white",
                          borderRadius: "8px",
                          fontFamily: "'Inter', sans-serif",
                          fontSize: "13px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: SECTION_THEMES.dateFilter.border,
                        },
                      }
                    }
                  }}
                />
              </Box>
            </Box>
          </LocalizationProvider>
        </SectionPaper>

        {/* ===== ACTION REQUIRED — Rose ===== */}
        <SectionPaper theme={SECTION_THEMES.action}>
          <SectionHeader
            title="Action Required"
            subtitle="Items requiring immediate attention"
            theme={SECTION_THEMES.action}
          />

          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={4}>
              <SplitStatusCard
                title="Support Tickets"
                icon={SupportAgentIcon}
                color="error"
                statuses={[
                  {
                    label: "Open",
                    value: adminStats?.openTickets ?? 0,
                    color: "error",
                    onClick: () => navigate("/tickets-list", { state: { txtStatus: "Open" } }),
                  },
                  {
                    label: "In Progress",
                    value: adminStats?.progressTickets ?? 0,
                    color: "warning",
                    onClick: () => navigate("/tickets-list", { state: { txtStatus: "In Progress" } }),
                  },
                ]}
                width="490px"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <MetricCard
                title="Pending Leave Requests"
                subtitle="Awaiting Approval"
                value={adminStats?.pendingLeave ?? 0}
                icon={EventNoteIcon}
                color="warning"
                onClick={() => navigate("/leave-list", { state: { status: "Pending" } })}
                showAction={true}
              />
            </Grid>
          </Grid>
        </SectionPaper>

        {/* ===== GREEN FELLOWS' SALARY INFORMATION — Emerald ===== */}
        <SectionPaper theme={{ ...SECTION_THEMES.salary, bg: "#ffffff", border: "#e2e8f0" }} sx={{ overflow: "hidden" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            mb={2}
            flexWrap="wrap"
            gap={2}
          >
            <SectionHeader
              title="Green Fellows' Salary Information"
              subtitle={`Salary status tracking from ${fromDate.format("MMM YYYY")} to ${toDate.format("MMM YYYY")}`}
              theme={SECTION_THEMES.salary}
            />
            <Box display="flex" gap={2} alignItems="center">
              <MDButton
                variant="outlined"
                size="small"
                startIcon={<IconTableExport size={18} />}
                onClick={handleExportSalary}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                  borderColor: SECTION_THEMES.salary.accent,
                  color: SECTION_THEMES.salary.accent,
                  "&:hover": {
                    borderColor: SECTION_THEMES.salary.accent,
                    bgcolor: SECTION_THEMES.salary.bg,
                  }
                }}
              >
                Export Excel
              </MDButton>
              <TextField
                select
                label="Global Status"
                value={salaryFilters.status}
                onChange={(e) =>
                  setSalaryFilters((prev) => ({ ...prev, status: e.target.value }))
                }
                size="small"
                SelectProps={{ native: true }}
                sx={{
                  minWidth: "150px",
                  "& .MuiInputBase-root": { fontSize: "13px", borderRadius: "8px" },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: SECTION_THEMES.salary.border },
                }}
              >
                <option value="All">All Status</option>
                <option value="Received">Received</option>
                <option value="Not received">Not received</option>
              </TextField>
            </Box>
          </Box>

          <MDBox
            sx={{
              maxHeight: "500px",
              overflow: "auto",
              width: "100%",
              border: `1px solid ${SECTION_THEMES.salary.border}`,
              borderRadius: "8px",
              "&::-webkit-scrollbar": { width: "6px", height: "6px" },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: SECTION_THEMES.salary.accent,
                borderRadius: "10px",
                opacity: 0.5,
              },
            }}
          >
            <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "12px 16px",
                      background: "#f8fafc",
                      borderBottom: `2px solid ${SECTION_THEMES.salary.border}`,
                      position: "sticky",
                      top: 0,
                      left: 0,
                      zIndex: 3,
                      minWidth: "180px",
                    }}
                  >
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleSort("user_name")}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: SECTION_THEMES.salary.subtitleColor,
                            textTransform: "uppercase",
                          }}
                        >
                          User Name
                        </Typography>
                        {salarySorting.column === "user_name" && (
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            {salarySorting.direction === "asc" ? "↑" : "↓"}
                          </Typography>
                        )}
                      </Box>
                      <TextField
                        select
                        size="small"
                        value={salaryFilters.user_id}
                        onChange={(e) =>
                          setSalaryFilters((prev) => ({ ...prev, user_id: e.target.value }))
                        }
                        SelectProps={{ native: true }}
                        sx={{ "& .MuiInputBase-root": { fontSize: "11px", height: "28px" } }}
                      >
                        <option value="">All Users</option>
                        {usersData?.map((u) => (
                          <option key={u.value} value={u.value}>
                            {u.title}
                          </option>
                        ))}
                      </TextField>
                    </Box>
                  </th>
                  <th
                    style={{
                      textAlign: "left",
                      padding: "12px 16px",
                      background: "#f8fafc",
                      borderBottom: `2px solid ${SECTION_THEMES.salary.border}`,
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                      minWidth: "140px",
                    }}
                  >
                    <Box display="flex" flexDirection="column" gap={1}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleSort("district_name")}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: SECTION_THEMES.salary.subtitleColor,
                            textTransform: "uppercase",
                          }}
                        >
                          District
                        </Typography>
                        {salarySorting.column === "district_name" && (
                          <Typography variant="caption" sx={{ ml: 0.5 }}>
                            {salarySorting.direction === "asc" ? "↑" : "↓"}
                          </Typography>
                        )}
                      </Box>
                      <TextField
                        select
                        size="small"
                        value={salaryFilters.district_id}
                        onChange={(e) =>
                          setSalaryFilters((prev) => ({ ...prev, district_id: e.target.value }))
                        }
                        SelectProps={{ native: true }}
                        sx={{ "& .MuiInputBase-root": { fontSize: "11px", height: "28px" } }}
                      >
                        <option value="">All Districts</option>
                        {districtData?.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.title}
                          </option>
                        ))}
                      </TextField>
                    </Box>
                  </th>
                  {monthColumns.map((m) => (
                    <th
                      key={m}
                      style={{
                        textAlign: "center",
                        padding: "12px 16px",
                        background: "#f8fafc",
                        borderBottom: `2px solid ${SECTION_THEMES.salary.border}`,
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                        minWidth: "110px",
                      }}
                    >
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            color: SECTION_THEMES.salary.subtitleColor,
                            textTransform: "uppercase",
                          }}
                        >
                          {dayjs(m).format("MMM YYYY")}
                        </Typography>
                        <TextField
                          select
                          size="small"
                          value={salaryFilters.monthFilters[m] || "All"}
                          onChange={(e) =>
                            setSalaryFilters((prev) => ({
                              ...prev,
                              monthFilters: { ...prev.monthFilters, [m]: e.target.value },
                            }))
                          }
                          SelectProps={{ native: true }}
                          sx={{ "& .MuiInputBase-root": { fontSize: "11px", height: "28px" } }}
                        >
                          <option value="All">All</option>
                          <option value="Received">Rec</option>
                          <option value="Not received">Not</option>
                        </TextField>
                      </Box>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {isSalaryLoading ? (
                  <tr>
                    <td
                      colSpan={monthColumns.length + 2}
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      <CircularProgress size={24} sx={{ color: SECTION_THEMES.salary.accent }} />
                    </td>
                  </tr>
                ) : sortedAndFilteredData.length > 0 ? (
                  sortedAndFilteredData.map((user) => (
                    <tr key={user.user_id} className="matrix-row">
                      <td
                        style={{
                          padding: "12px 16px",
                          borderBottom: `1px solid ${SECTION_THEMES.salary.border}`,
                          position: "sticky",
                          left: 0,
                          backgroundColor: "white",
                          zIndex: 1,
                        }}
                      >
                        <Typography
                          variant="button"
                          sx={{ fontWeight: 600, color: COLORS.text.primary, fontSize: "13px" }}
                        >
                          {user.user_name}
                        </Typography>
                      </td>
                      <td
                        style={{
                          padding: "12px 16px",
                          borderBottom: `1px solid ${SECTION_THEMES.salary.border}`,
                        }}
                      >
                        <Typography
                          variant="button"
                          sx={{ color: COLORS.text.secondary, fontSize: "12px" }}
                        >
                          {user.district_name}
                        </Typography>
                      </td>
                      {monthColumns.map((m) => {
                        const history = user.salary_history?.find((h) => h.month === m);
                        const status = history ? history.status : "Not received";
                        return (
                          <td
                            key={m}
                            style={{
                              padding: "12px 16px",
                              borderBottom: `1px solid ${SECTION_THEMES.salary.border}`,
                              textAlign: "center",
                            }}
                          >
                            <StatusIndicator status={status} />
                          </td>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={monthColumns.length + 2}
                      style={{ textAlign: "center", padding: "40px" }}
                    >
                      <Typography variant="button" sx={{ color: COLORS.text.muted }}>
                        No records found matching your filters.
                      </Typography>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </MDBox>
          <style>{`
            .matrix-row:hover td { background-color: #f8fafc !important; }
            .matrix-row td { transition: background-color 0.2s; }
          `}</style>
        </SectionPaper>

        <Snackbar
          open={exportSuccess}
          autoHideDuration={4000}
          onClose={handleCloseAlert}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert onClose={handleCloseAlert} severity="success" sx={{ width: "100%" }}>
            Salary Report exported successfully!
          </Alert>
        </Snackbar>
        <Backdrop open={isFetchingSubmission || isFetchingSalary} sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <Lottie options={defaultOptions} height={250} width={250} />
            <Typography variant="h4" color={"green"} mt={-5}>
              Loading
            </Typography>
          </Box>
        </Backdrop>
      </Box>
      <Footer />
    </DashboardLayout>
  );
  
}

export default Dashboard;